/**
 * 首页相关接口
 * 对应 Apipost: 首页 > 推荐列表
 */

import { http } from "@/http/axios";

// ==================== 类型定义 ====================

/** 推荐商品类型：tour=线路 guide=导游 hotel=酒店 car=租车 */
export type RecommendProductType = "tour" | "guide" | "hotel" | "car";

/** 推荐列表单条数据 */
export interface RecommendProduct {
  id: number;
  /** 商品类型 */
  type: RecommendProductType;
  /** 商品名称 */
  name: string;
  /** 封面图 */
  cover_image: string;
  /** 价格（导游为 0，酒店为 null） */
  price: number | null;
  /** 价格展示文案，如「¥1999.00/人起」「面议」「查看房型」 */
  price_text: string;
  /** 副标题/描述，如「成都·3天2晚·川西」 */
  desc: string;
  /** 排序值 */
  sort: number;
  /** 距离（km），可能为 null */
  distance: number | null;
  /** 距离展示文案，如「17.3km」 */
  distance_text: string | null;
}

/** 推荐列表响应 */
export interface RecommendProductListResponse {
  code: number;
  message: string;
  data: {
    list: RecommendProduct[];
    total: number;
    page: number;
    limit: number;
    time: number;
    has_more: boolean;
  };
}

/** 推荐列表请求参数 */
export interface RecommendProductListParams {
  /** 城市名，如「成都」 */
  city?: string;
  /** 经度 */
  longitude?: string | number;
  /** 纬度 */
  latitude?: string | number;
  /** 商品类型：all=全部 */
  type?: RecommendProductType | "all";
  page?: number;
  limit?: number;
}

/** 首页旅游推荐响应（data 直接为数组，无分页） */
export interface TourRecommendResponse {
  code: number;
  message: string;
  data: RecommendProduct[];
}

/** 首页旅游推荐请求参数 */
export interface TourRecommendParams {
  /** 城市名，如「成都」 */
  city?: string;
  /** 经度 */
  longitude?: string | number;
  /** 纬度 */
  latitude?: string | number;
}

// ==================== 接口调用 ====================

/**
 * 获取首页推荐列表
 * GET /home/index/productList
 *
 * @param params 查询参数（city / longitude / latitude / type / page / limit）
 */
export async function fetchRecommendProductList(
  params: RecommendProductListParams = {},
): Promise<RecommendProductListResponse["data"]> {
  const { city, longitude, latitude, type = "all", page = 1, limit = 15 } = params;

  const res = await http.get<RecommendProductListResponse>(
    "/home/index/productList",
    {
      params: {
        ...(city ? { city } : {}),
        ...(longitude != null ? { longitude } : {}),
        ...(latitude != null ? { latitude } : {}),
        type,
        page,
        limit,
      },
    },
  );

  if (res.data.code !== 200) {
    throw new Error(res.data.message || "获取推荐列表失败");
  }

  return res.data.data;
}

/**
 * 获取首页「为你推荐」旅游列表
 * GET /home/index/tourRecommend
 *
 * 与 productList 的区别：本接口 data 直接为数组，一次性返回全部推荐（无分页）。
 * 后端会依据传入的经纬度计算距离并排序（distance / distance_text 字段）。
 *
 * @param params 查询参数（city / longitude / latitude）
 */
export async function fetchTourRecommend(
  params: TourRecommendParams = {},
): Promise<RecommendProduct[]> {
  const { city, longitude, latitude } = params;

  const res = await http.get<TourRecommendResponse>(
    "/home/index/tourRecommend",
    {
      params: {
        ...(city ? { city } : {}),
        ...(longitude != null ? { longitude } : {}),
        ...(latitude != null ? { latitude } : {}),
      },
    },
  );

  if (res.data.code !== 200) {
    throw new Error(res.data.message || "获取旅游推荐失败");
  }

  return res.data.data ?? [];
}
